/**
 * Граница ошибок.
 *
 * До неё любое исключение в рендере размонтировало всё дерево: человек видел
 * белый экран без единого слова и без пути назад. Для офлайн-приложения это
 * особенно жестоко — данные на месте, но выглядит так, будто их больше нет.
 *
 * Отдельно разбирается срыв ленивой загрузки экрана. После выката новой версии
 * старые файлы с сервера исчезают, а вкладка, открытая до выката, продолжает
 * их запрашивать — переход в раздел падает. Это не поломка приложения, а
 * устаревшая вкладка, и говорить об этом надо иначе.
 */

import { RefreshCw } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

import { isChunkLoadError } from '@/shared/lib/chunk-error';
import { record } from '@/shared/lib/diagnostics';
import { Button } from '@/shared/ui/Button';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Название места — попадает в сообщение об ошибке в консоли. */
  area?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    const area = this.props.area ?? 'приложение';
    // В журнал на устройстве: консоль на телефоне никто не открывает, а без
    // записи причину сбоя потом не восстановить вообще никак.
    record('error', `рендер ${area}`, error);
    console.error(`Сбой в разделе «${area}»`, error, info.componentStack);
  }

  override render() {
    if (!this.state.error) return this.props.children;

    return <ErrorScreen error={this.state.error} onRetry={() => this.setState({ error: null })} />;
  }
}

function ErrorScreen({ error, onRetry }: { error: Error; onRetry: () => void }) {
  const outdated = isChunkLoadError(error);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
      <h1 className="text-[20px] font-semibold">
        {outdated ? 'Вышла новая версия' : 'Что-то пошло не так'}
      </h1>

      <p className="max-w-sm text-[15px] text-muted">
        {outdated
          ? 'Эта вкладка открыта давно, и часть приложения больше не загружается. Обновите страницу — данные на устройстве не пострадали.'
          : 'Экран не смог отрисоваться. Данные на устройстве целы: они хранятся отдельно от интерфейса.'}
      </p>

      <div className="flex flex-col gap-2 pt-2">
        <Button
          size="lg"
          iconLeft={<RefreshCw size={18} aria-hidden />}
          onClick={() => window.location.reload()}
        >
          Обновить страницу
        </Button>

        {!outdated && (
          <Button variant="secondary" size="lg" onClick={onRetry}>
            Попробовать ещё раз
          </Button>
        )}
      </div>

      {/* Текст ошибки — не для красоты: без сборщика ошибок это единственное,
          что человек может переслать, когда сообщает о сбое. */}
      <p className="max-w-sm break-words pt-2 text-[12px] text-subtle">{error.message}</p>
    </div>
  );
}
